import { useEffect, useState } from 'react';

type OnEndReachedCallback = void | null | undefined | (() => void | Promise<unknown>);

interface Props {
	onEndReached?: OnEndReachedCallback;
	containerRef?: HTMLElement;
	inverted?: boolean | null;
}

const OFFSET_IN_PIXELS = 150;

export const useScrollEndReached = ({ onEndReached, containerRef, inverted = false }: Props) => {
	const [isFetching, setIsFetching] = useState(false);

	useEffect(() => {
		const domElement = containerRef || window;
		function handleScroll() {
			const isEndReached = getIsEndReached(inverted || undefined, containerRef);
			if (!isEndReached) {
				return;
			}

			setIsFetching(true);
		}

		domElement.addEventListener('scroll', handleScroll);

		return () => domElement.removeEventListener('scroll', handleScroll);
	}, [containerRef, inverted]);

	useEffect(() => {
		const loadMore = async () => {
			if (!isFetching) {
				return;
			}

			try {
				onEndReached && (await onEndReached());
			} finally {
				setIsFetching(false);
			}
		};

		void loadMore();
	}, [isFetching, onEndReached]);
};

function getIsEndReached(inverted = false, domElement?: HTMLElement) {
	if (domElement) {
		return inverted ? getIsTopReached(domElement) : getIsBottomReached(domElement);
	}

	return inverted ? getIsWindowStartReached() : getIsWindowEndReached();
}

function getIsWindowStartReached() {
	return window.pageYOffset <= 0 + OFFSET_IN_PIXELS;
}

function getIsWindowEndReached() {
	return (
		window.innerHeight + window.pageYOffset + OFFSET_IN_PIXELS >=
		document.documentElement.scrollHeight
	);
}

function getIsBottomReached(domElement: HTMLElement) {
	return (
		domElement.scrollTop + OFFSET_IN_PIXELS >= domElement.scrollHeight - domElement.offsetHeight
	);
}

function getIsTopReached(domElement: HTMLElement) {
	return domElement.scrollTop <= 0 + OFFSET_IN_PIXELS;
}
